import { Formik } from 'formik'
import { KeyboardEvent, ReactElement } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Input } from 'components/Input'
import { ClickAwayListener } from 'components/v2/ClickAwayListener'
import { ICoordinatesFormValues } from 'features/RoutingProjects/components/LocationDetails/CoordinatesForm/types'
import { schema } from './schema'

const SubmitButton = styled.button`
  display: none;
`

const messages = defineMessages({
  coordinatePlacholder: {
    id: 'features.RoutingProjects.components.LocationDetails.CoordinatesForm.coordinatePlacholder',
    defaultMessage: 'E.g. 52.5200, 13.4050'
  }
})

interface ICoordinatesFormProps {
  coordinates: {
    latitude: number
    longitude: number
  }
  onClickAway: () => void
  onSubmit: (coordinates: { latitude: number; longitude: number }) => void
}

export const CoordinatesForm = ({ coordinates, onClickAway, onSubmit }: ICoordinatesFormProps): ReactElement => {
  const intl = useIntl()

  const initialValues: ICoordinatesFormValues = {
    coordinates: `${coordinates.latitude}, ${coordinates.longitude}`
  }

  const handleSubmit = (values: ICoordinatesFormValues): void => {
    if (values.coordinates === initialValues.coordinates) {
      onClickAway()
      return
    }
    const [latitude, longitude] = values.coordinates
      .split(',')
      .map((coordinate): number => parseFloat(coordinate.trim()))
    onSubmit({ latitude, longitude })
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
      {({ values, handleChange, handleSubmit, touched, errors }): ReactElement => {
        const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
          if (event.key === 'Escape') {
            onClickAway()
          }
        }

        return (
          <form onSubmit={handleSubmit}>
            <ClickAwayListener onClickAway={onClickAway}>
              <Input
                id="coordinates"
                placeholder={intl.formatMessage(messages.coordinatePlacholder)}
                value={values.coordinates}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                errors={errors.coordinates}
                touched={touched.coordinates}
                small
                autoFocus
              />
            </ClickAwayListener>
            <SubmitButton type="submit"></SubmitButton>
          </form>
        )
      }}
    </Formik>
  )
}
